import axiosInstance from 'axiosInstance';

const PUSH_PERMISSION_ASKED_PREFIX = 'gestodoPushPermissionAsked';

const getPermissionAskedKey = userId => `${PUSH_PERMISSION_ASKED_PREFIX}:${userId || 'anonymous'}`;

const hasAskedPermission = userId => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(getPermissionAskedKey(userId)) === 'true';
  } catch (error) {
    return false;
  }
};

const markPermissionAsked = userId => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(getPermissionAskedKey(userId), 'true');
  } catch (error) {
    return;
  }
};

const urlBase64ToUint8Array = base64String => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const isPushSupported = () => (
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window
);

const getPushConfig = async () => {
  const response = await axiosInstance.get('notifications/push/config');
  return response.data || {};
};

export const attachPushSubscription = async userId => {
  if (!isPushSupported()) {
    return { status: 'unsupported' };
  }

  const config = await getPushConfig();
  if (!config.enabled) {
    return { status: 'disabled' };
  }

  const vapidPublicKey = String(config.vapid_public_key || '').trim();
  if (!vapidPublicKey) {
    return { status: 'missing_vapid_public_key' };
  }

  if (Notification.permission === 'default' && !hasAskedPermission(userId)) {
    markPermissionAsked(userId);
    try {
      await Notification.requestPermission();
    } catch (error) {
      return { status: 'permission_request_failed' };
    }
  }

  if (Notification.permission !== 'granted') {
    return { status: 'permission_not_granted' };
  }

  const registration = await navigator.serviceWorker.register('/push-sw.js');
  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
  }

  await axiosInstance.post('notifications/push/subscribe', {
    subscription: subscription.toJSON()
  });

  return { status: 'subscribed' };
};

export const detachPushSubscription = async () => {
  if (!isPushSupported()) {
    return { status: 'unsupported' };
  }

  const registration = await navigator.serviceWorker.getRegistration('/push-sw.js');
  if (!registration) {
    return { status: 'no_registration' };
  }

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return { status: 'no_subscription' };
  }

  const endpoint = subscription.endpoint;

  try {
    await axiosInstance.post('notifications/push/unsubscribe', { endpoint });
  } catch (error) {
    // Continue with browser unsubscribe even if backend call fails.
  }

  try {
    await subscription.unsubscribe();
  } catch (error) {
    return { status: 'unsubscribe_failed' };
  }

  return { status: 'unsubscribed' };
};
